import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiMenu } from 'react-icons/fi';
import LanguageDropdown from './LanguageDropdown';
import useTranslation from '../hooks/useTranslation';
import useTheme from '../hooks/useTheme';

function ProfileDropdown({ open, onClose, onUserDetails, onReport, onSubscription, onDeleteAccount, onLogout }) {
  const { t } = useTranslation();
  useEffect(() => {
    if (!open) return;
    function handleKey(e) {
      if (e.key === "Escape") onClose();
    }
    function handleClick(e) {
      if (e.target.classList.contains("profile-dropdown-bg")) onClose();
    }
    window.addEventListener("keydown", handleKey);
    document.addEventListener("mousedown", handleClick);
    return () => {
      window.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [open, onClose]);
  return (
    <div className={`profile-dropdown-bg${open ? ' open' : ''}`}>
      <div className={`profile-dropdown-box${open ? ' open' : ''}`}>
        <button className="profile-dropdown-btn report-btn" onClick={onUserDetails}>{t('User Details')}</button>
        <button className="profile-dropdown-btn report-btn" onClick={onReport}>{t('Report')}</button>
        <button className="profile-dropdown-btn report-btn" onClick={onSubscription}>{t('Subscription')}</button>
        <button
          className="profile-dropdown-btn delete-account-btn"
          style={{ fontWeight: 700 }}
          onClick={onDeleteAccount}
        >
          {t('Deactivate Account')}
        </button>
        <button className="profile-dropdown-btn logout-btn" onClick={onLogout}>{t('Logout')}</button>
      </div>
    </div>
  );
}

export default function HomeNavbar({
  userName,
  userId,
  profileOpen,
  setProfileOpen,
  handleLogout,
  showLoginModal,
  setShowLoginModal,
  isMobile,
  mobileMenuOpen,
  setMobileMenuOpen,
  mobileProfileOpen,
  setMobileProfileOpen,
  mobileLanguageOpen,
  setMobileLanguageOpen,
  setShowHelpModal,
  toggleTheme,
  setUserDetailsModalOpen,
  setShowDeleteModal,
  setShowSubscriptionModal,
  setSubscriptionPlan,
  userDetails,
  isSchoolAdmin,
  isHospitalAdmin,
  isHospitalDoctor,
  navbarClassName,
  getFirstPlanForRole,
}) {
  const { t, setLanguage, language } = useTranslation();
  const { theme } = useTheme();

  return (
    <nav className={navbarClassName}>
      <span className="logo">
        <img src="/logoupdated.png" alt="" className="logo-icon" />
        <span className="logo-text-wrapper">
          <span className="logo-text">{t('Neurolonic')}</span>
          <span className="logo-beta-label">{t('Beta')}</span>
        </span>
      </span>
      {(localStorage.getItem('neuro_display_name') || userName) && !isSchoolAdmin && (
        <span className="neon-username">
          {t('Hi,')} {localStorage.getItem('neuro_display_name') || userName}!
        </span>
      )}
      <div className="navbar-actions">
        {!isMobile && userId && (<>
          {userDetails?.role && userDetails.role.toLowerCase().includes('individual') && (
            <Link to="/dashboard" className="dashboard-btn">
              {t('Dashboard')}
            </Link>
          )}
          <button className={`get-started-btn${profileOpen ? ' active' : ''}`} style={{ position: 'relative' }} onClick={() => setProfileOpen((v) => !v)}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ verticalAlign: 'middle' }}>
              <circle cx="12" cy="8" r="4.5" stroke="currentColor" strokeWidth="2" />
              <path d="M4.5 19.5c0-3.5 3.5-6 7.5-6s7.5 2.5 7.5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
          <button aria-label={t('Help')} className="dashboard-btn help-btn" onClick={() => setShowHelpModal(true)}>
            ?
          </button>
          <button aria-label={t('Toggle theme')} className="dashboard-btn theme-toggle-btn" onClick={toggleTheme} style={{ marginLeft: 4 }} title={`${t('Theme')}: ${theme.charAt(0).toUpperCase() + theme.slice(1)}`}>{theme === "light" && <span role="img" aria-label={t('Light')}>🌞</span>}{theme === "dark" && <span role="img" aria-label={t('Dark')}>🌜</span>}{theme === "device" && <span role="img" aria-label={t('Device')}>💻</span>}</button>
          <LanguageDropdown currentLanguage={language} onLanguageChange={setLanguage} theme={theme} />
          <ProfileDropdown open={profileOpen} onClose={() => setProfileOpen(false)} onUserDetails={() => { setProfileOpen(false); setUserDetailsModalOpen(true); }} onReport={() => { setProfileOpen(false); }} onSubscription={() => { setProfileOpen(false); let role = userDetails?.role || userDetails?.user_role || localStorage.getItem("neuro_user_role") || "Individual"; setSubscriptionPlan(getFirstPlanForRole(role)); setShowSubscriptionModal(true); }} onDeleteAccount={() => { setProfileOpen(false); setShowDeleteModal(true); }} onLogout={() => { setProfileOpen(false); handleLogout(); }} />
        </>
        )}
        {!isMobile && !userId && (
          <>
            <button className="dashboard-btn" onClick={() => setShowLoginModal(true)}>{t('Login')}</button>
            <Link to="/getstarted" className="get-started-btn">{t('Get Started')}</Link>
            <button aria-label={t('Help')} className="dashboard-btn help-btn" onClick={() => setShowHelpModal(true)}>?</button>
            <button aria-label={t('Toggle theme')} className="dashboard-btn theme-toggle-btn" onClick={toggleTheme} style={{ marginLeft: 4 }} title={`${t('Theme')}: ${theme.charAt(0).toUpperCase() + theme.slice(1)}`}>{theme === "light" && <span role="img" aria-label={t('Light')}>🌞</span>}{theme === "dark" && <span role="img" aria-label={t('Dark')}>🌜</span>}{theme === "device" && <span role="img" aria-label={t('Device')}>💻</span>}</button>
            <LanguageDropdown currentLanguage={language} onLanguageChange={setLanguage} theme={theme} />
          </>
        )}
        {isMobile && (
          <>
            {userId && !isHospitalAdmin && !isHospitalDoctor && (
              <Link to="/dashboard" className="dashboard-btn mobile-dashboard-btn" style={{
                borderRadius: 18,
                minWidth: 0,
                height: 38,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--color-bg, #fff)',
                color: 'var(--color-text, #232323)',
                border: '2px solid #fff',
                marginRight: 8,
                marginLeft: 0,
                fontSize: 16,
                fontWeight: 600,
                padding: '0 1.2rem',
                boxShadow: '0 1.5px 8px #0002',
                position: 'relative',
                zIndex: 1001,
                letterSpacing: 0.2,
                textDecoration: 'none',
                transition: 'background 0.18s, color 0.18s, border 0.18s',
                whiteSpace: 'nowrap',
              }} aria-label={t('Dashboard')}>
                {t('Dashboard')}
              </Link>
            )}
            {!userId && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 6 }}>
                <button className="dashboard-btn mobile-login-btn" style={{ height: 32, fontSize: 14, padding: '0 0.8rem', borderRadius: 16 }} onClick={() => setShowLoginModal(true)}>{t('Login')}</button>
                <Link to="/getstarted" className="get-started-btn mobile-getstarted-btn" style={{ height: 32, fontSize: 14, padding: '0 0.8rem', borderRadius: 16 }}>{t('Get Started')}</Link>
              </div>
            )}
            <button className="mobile-menu-btn" aria-label="Open menu" style={{ background: 'none', border: 'none', padding: 0, marginLeft: 0, fontSize: 28, color: 'var(--color-text, #232323)', borderRadius: '50%', width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1001 }} onClick={() => setMobileMenuOpen((v) => !v)}>
              <FiMenu size={28} />
            </button>
            {mobileMenuOpen && (
              <div className="mobile-menu-dropdown">
                {userId && (
                  <button className="dashboard-btn mobile-menu-icon-btn" onClick={() => { setMobileProfileOpen(true); setMobileLanguageOpen(false); setMobileMenuOpen(false); }}
                    title={t('Profile')} aria-label={t('Profile')}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ verticalAlign: 'middle' }}><circle cx="12" cy="8" r="4.5" stroke="currentColor" strokeWidth="2" /><path d="M4.5 19.5c0-3.5 3.5-6 7.5-6s7.5 2.5 7.5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                  </button>
                )}
                <button className="dashboard-btn theme-toggle-btn mobile-menu-icon-btn" onClick={() => { toggleTheme(); }}
                  title={t('Theme')} aria-label={t('Theme')}>
                  {theme === "light" && <span role="img" aria-label={t('Light')} className="icon-emoji">🌞</span>}
                  {theme === "dark" && <span role="img" aria-label={t('Dark')} className="icon-emoji">🌙</span>}
                  {theme === "device" && <span role="img" aria-label={t('Device')} className="icon-emoji">💻</span>}
                </button>
                <button className="dashboard-btn mobile-menu-icon-btn" onClick={() => { setMobileLanguageOpen(true); setMobileProfileOpen(false); setMobileMenuOpen(false); }}
                  title={t('Language')} aria-label={t('Language')}>
                  <span role="img" aria-label={t('Language')} className="icon-emoji">🌐</span>
                </button>
                <button className="dashboard-btn help-btn mobile-menu-icon-btn" onClick={() => { setShowHelpModal(true); setMobileMenuOpen(false); }}
                  title={t('Help')} aria-label={t('Help')}>
                  <span className="icon-question" style={{ fontSize: 22, fontWeight: 700, lineHeight: 1 }}>?</span>
                </button>
              </div>
            )}
            {isMobile && mobileProfileOpen && (
              <div className="mobile-profile-dropdown" onClick={e => e.stopPropagation()}>
                <button className="dropdown-close-btn" aria-label="Close" onClick={() => setMobileProfileOpen(false)}>
                  ×
                </button>
                <button className="profile-dropdown-btn report-btn" onClick={() => { setMobileProfileOpen(false); setUserDetailsModalOpen(true); }}>{t('User Details')}</button>
                <button className="profile-dropdown-btn report-btn" onClick={() => { setMobileProfileOpen(false); }}>{t('Report')}</button>
                <button className="profile-dropdown-btn report-btn" onClick={() => { setMobileProfileOpen(false); let role = userDetails?.role || userDetails?.user_role || localStorage.getItem("neuro_user_role") || "Individual"; setSubscriptionPlan(getFirstPlanForRole(role)); setShowSubscriptionModal(true); }}>{t('Subscription')}</button>
                <button className="profile-dropdown-btn delete-account-btn" style={{ fontWeight: 700 }} onClick={() => { setMobileProfileOpen(false); setShowDeleteModal(true); }}>{t('Deactivate Account')}</button>
                <button className="profile-dropdown-btn logout-btn" onClick={() => { setMobileProfileOpen(false); handleLogout(); }}>{t('Logout')}</button>
              </div>
            )}
            {isMobile && mobileLanguageOpen && (
              <div className="mobile-language-dropdown" onClick={e => e.stopPropagation()}>
                <button className="dropdown-close-btn" aria-label="Close" onClick={() => setMobileLanguageOpen(false)}>
                  ×
                </button>
                <div style={{ paddingBottom: '1rem' }}>
                  {[
                    { code: 'en', label: 'English' },
                    { code: 'de', label: 'German' },
                    { code: 'fr', label: 'French' },
                    { code: 'es', label: 'Spanish' },
                    { code: 'nl', label: 'Dutch' },
                    { code: 'pt', label: 'Portuguese' },
                    { code: 'ru', label: 'Russian' },
                    { code: 'ja', label: 'Japanese' },
                    { code: 'hi', label: 'Hindi' },
                    { code: 'ta', label: 'Tamil' },
                    { code: 'ar', label: 'Arabic' },
                    { code: 'ur', label: 'Urdu' },
                    { code: 'zh', label: 'Chinese (Mandarin)' },
                    { code: 'it', label: 'Italian' },
                    { code: 'ko', label: 'Korean' },
                  ].map(lang => (
                    <div
                      key={lang.code}
                      onClick={() => { setLanguage(lang.code); setMobileLanguageOpen(false); }}
                      style={{
                        padding: '0.6rem 1.1rem',
                        borderRadius: 10,
                        fontWeight: 500,
                        fontSize: '1.08rem',
                        cursor: 'pointer',
                        marginBottom: 2,
                        textAlign: 'left',
                        background: language === lang.code ? '#f5b443' : 'transparent',
                        color: language === lang.code ? '#fff' : 'inherit',
                      }}
                      tabIndex={0}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setLanguage(lang.code);
                          setMobileLanguageOpen(false);
                        }
                      }}
                    >
                      {lang.label}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </nav>
  );
}
